import { PortableText, type PortableTextBlock } from 'next-sanity';

import { Link } from '#/components/Link';

type LegalTableCell = {
  _key: string;
  content: PortableTextBlock[];
};

type LegalTableRow = {
  _key: string;
  cells: LegalTableCell[];
};

export type LegalTableProps = {
  title?: string;
  isFirstRowHeader: boolean;
  rows: LegalTableRow[];
};

const tableCellComponents = {
  block: {
    normal: ({ children }: { children: React.ReactNode }) => (
      <span className="block">{children}</span>
    ),
  },
  marks: {
    internalLink: ({
      value,
      children,
    }: {
      value: { href: string; target?: string };
      children: React.ReactNode;
    }) => (
      <Link
        className="underline underline-offset-2"
        href={value.href}
        target={value.target}
      >
        {children}
      </Link>
    ),
    externalLink: ({
      value,
      children,
    }: {
      value: { href: string; target?: string };
      children: React.ReactNode;
    }) => (
      <Link
        className="underline underline-offset-2"
        href={value.href}
        target={value.target}
      >
        {children}
      </Link>
    ),
  },
};

function CellContent({ content }: { content: PortableTextBlock[] }) {
  if (!content || content.length === 0) {
    return <>&nbsp;</>;
  }
  return (
    <PortableText
      value={content}
      components={
        tableCellComponents as React.ComponentProps<
          typeof PortableText
        >['components']
      }
    />
  );
}

export function LegalTable({ title, isFirstRowHeader, rows }: LegalTableProps) {
  const headerRow = isFirstRowHeader ? rows?.[0] : null;
  const bodyRows = isFirstRowHeader ? rows?.slice(1) : rows;

  return (
    <>
      {title && (
        <h3 className="text-left pl-10 text-26 md:text-32 font-[440] mb-20">
          {title}
        </h3>
      )}
      <div className="overflow-x-auto">
        <table className="table-auto p-10 min-w-full text-14 m:text-16">
          {headerRow && (
            <thead className="text-left">
              <tr className="border-b font-[440]">
                {headerRow.cells.map((cell) => (
                  <th
                    key={cell._key}
                    className="font-[440] py-10 px-12 whitespace-nowrap"
                  >
                    <CellContent content={cell.content} />
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {bodyRows?.map((row) => (
              <tr key={row._key} className="border-b">
                {row.cells.map((cell) => (
                  <td
                    key={cell._key}
                    className="py-10 px-12 whitespace-pre-wrap"
                  >
                    <CellContent content={cell.content} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
